/**
 * TanStack Query hooks for the ADR-038 data-query surface (Phase F).
 *
 * Two reads, both against the BFF and nothing else (REQ-UI-035):
 *
 * - :func:`useQueryCatalog` — ``GET /api/query/catalog``, the role-filtered
 *   inventory of queryable types and their typed attributes. Cached for a
 *   long stale time because the registry is static in-process; the one
 *   moving part is the status types' data-derived domain-metric group,
 *   which the BFF already serves from its own short-TTL cache (L6).
 * - :func:`useQueryRecords` — ``POST /api/query/records``. A POST that is
 *   semantically a **read**: the predicate AST does not fit query
 *   parameters, and a GET would spill operator-entered filter values into
 *   access logs, proxy logs, and browser history (D3). It mutates nothing
 *   and emits ``query.executed`` rather than a configuration-change event,
 *   which is why it belongs in ``useQuery`` and not ``useMutation``.
 *
 * # Cancellation is load-bearing here
 *
 * Auto-run (D14) re-issues on every committed chip edit. Both hooks thread
 * TanStack's ``signal`` into ``apiFetch``, so a superseded request is
 * aborted rather than left racing its successor — otherwise a slow earlier
 * query could land after a faster later one and show the operator results
 * for a predicate they have already changed.
 *
 * Cross-references:
 *   - REQ-QUERY-029..031 (catalog + records endpoints); REQ-AUTH-075/076.
 *   - REQ-UI-117 (validity-gated auto-run); REQ-UI-035 (BFF-only calls).
 *   - Feature spec §2.4; ADR-038 §Decision 1 / §Decision 2.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import type {
  QueryCatalog,
  RecordsResponse,
  TimeRangeSelection,
  WirePredicate,
} from "@/query/query-model";
import { toWireTimeRange } from "@/query/query-model";
import type { SortState } from "@/data-table/data-table-model";

/**
 * How long a fetched catalog stays fresh. The registry is static in the
 * BFF process, so the only churn is the dynamic domain-metric group; five
 * minutes keeps a long Query session from re-fetching an unchanging
 * inventory on every panel remount.
 */
const CATALOG_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * How long a result page stays fresh. Short: the store is live, and an
 * operator who re-selects the same window is asking "what does it say
 * now?", not "show me the cached answer".
 */
const RECORDS_STALE_TIME_MS = 5 * 1000;

/** The records request as it goes on the wire. */
export interface RecordsRequest {
  readonly type: string;
  readonly timeRange: TimeRangeSelection;
  readonly predicate: WirePredicate | null;
  readonly sort: SortState;
  readonly pageIndex: number;
  readonly pageSize: number;
}

/** A BFF rejection the panel can render against the offending chip. */
export interface QueryErrorDetail {
  /** Machine code, e.g. ``invalid_predicate`` / ``invalid_query``. */
  readonly error: string;
  /** Operator-facing explanation from the BFF. */
  readonly message: string;
  /**
   * Dotted path to the offending predicate node, when the BFF located one
   * (``invalid_predicate`` only). The panel marks that chip (REQ-UI-117).
   */
  readonly path?: string;
}

/**
 * Extract the BFF's structured error detail from an :class:`ApiError`.
 *
 * The query routes reject with ``detail: {error, message, path?}``; FastAPI
 * wraps that under ``detail``. Returns ``null`` for anything that does not
 * match, so an unexpected error body degrades to the generic message
 * rather than rendering ``undefined`` at the operator.
 */
export function queryErrorDetail(error: unknown): QueryErrorDetail | null {
  if (!(error instanceof ApiError)) return null;
  const body = error.body;
  if (typeof body !== "object" || body === null) return null;
  const detail = (body as Record<string, unknown>).detail;
  if (typeof detail !== "object" || detail === null) return null;
  const rec = detail as Record<string, unknown>;
  if (typeof rec.error !== "string" || typeof rec.message !== "string") return null;
  return {
    error: rec.error,
    message: rec.message,
    path: typeof rec.path === "string" && rec.path !== "" ? rec.path : undefined,
  };
}

/** Render the records request body. */
export function recordsRequestBody(request: RecordsRequest): Record<string, unknown> {
  return {
    type: request.type,
    time_range: toWireTimeRange(request.timeRange),
    predicate: request.predicate,
    sort: request.sort.map((rule) => ({
      attribute: rule.key,
      direction: rule.direction,
    })),
    page: { index: request.pageIndex, size: request.pageSize },
  };
}

/** Fetch the role-filtered catalog of queryable types. */
export function useQueryCatalog(): UseQueryResult<QueryCatalog, ApiError> {
  return useQuery<QueryCatalog, ApiError>({
    queryKey: ["query", "catalog"],
    queryFn: async ({ signal }) => {
      const raw = await apiFetch<unknown>("/api/query/catalog", { signal });
      if (!isCatalog(raw)) {
        console.error("[meridian/query/use-query-data] catalog failed shape validation", raw);
        throw new ApiError(500, raw, "catalog response did not match the expected shape");
      }
      return raw;
    },
    staleTime: CATALOG_STALE_TIME_MS,
    retry: (failureCount, error) =>
      error instanceof ApiError && (error.status === 401 || error.status === 403)
        ? false
        : failureCount < 2,
  });
}

/**
 * Run one records query.
 *
 * ``enabled`` is the panel's validity gate (D14): a query with a
 * half-typed chip or an unresolved time window must not issue a request at
 * all. A 400 from the BFF is **not retried** — the predicate is wrong, and
 * retrying it just re-runs a rejection.
 */
export function useQueryRecords(
  request: RecordsRequest | null,
  options: { enabled: boolean },
): UseQueryResult<RecordsResponse, ApiError> {
  const body = request === null ? null : recordsRequestBody(request);
  return useQuery<RecordsResponse, ApiError>({
    queryKey: ["query", "records", body],
    queryFn: async ({ signal }) => {
      if (body === null) {
        throw new ApiError(0, null, "no query to run");
      }
      const raw = await apiFetch<unknown>("/api/query/records", {
        method: "POST",
        body: JSON.stringify(body),
        signal,
      });
      if (!isRecordsResponse(raw)) {
        console.error("[meridian/query/use-query-data] records failed shape validation", raw);
        throw new ApiError(500, raw, "records response did not match the expected shape");
      }
      return raw;
    },
    enabled: options.enabled && body !== null,
    staleTime: RECORDS_STALE_TIME_MS,
    // Keep the previous page on screen while the next one loads, so an
    // auto-run re-query does not blank the table under the operator — but
    // only when it is a page of the SAME type. A page of another type carried
    // as placeholder renders that type's rows under this type's columns:
    // every attribute the new type declares reads as absent, and
    // ``queryRowKey`` (built from the new type's id + time attributes) finds
    // nothing to key on beyond a shared ``producer_id``, so the whole page
    // collapses to one React key. React's documented response to duplicate
    // keys is to duplicate or omit children, and on the rig it duplicated:
    // the stale ``<tr>``s survived the real page's arrival as orphan DOM
    // nodes with no handlers — rows an operator could see but not select,
    // that outlived every later type switch until a reload (2026-10-19).
    // Across types the honest display is the empty state's "Running…"
    // (CLAUDE.md §8.1 — never a frame that looks like data).
    placeholderData: (previous, previousQuery) => {
      const previousBody = previousQuery?.queryKey[2] as { type?: unknown } | null | undefined;
      return previousBody?.type === body?.type ? previous : undefined;
    },
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 1;
    },
  });
}

/** Shape guard at the trust boundary (coding-standards §2.2). */
function isCatalog(value: unknown): value is QueryCatalog {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    Array.isArray(obj.types) &&
    Array.isArray(obj.page_size_options) &&
    typeof obj.max_comparisons === "number" &&
    typeof obj.max_nesting_depth === "number"
  );
}

/** Shape guard at the trust boundary (coding-standards §2.2). */
function isRecordsResponse(value: unknown): value is RecordsResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return (
    Array.isArray(obj.items) &&
    typeof obj.total === "number" &&
    typeof obj.total_is_lower_bound === "boolean" &&
    Array.isArray(obj.tiers_consulted)
  );
}
